import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Map } from 'immutable';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { makeSelectUser } from 'containers/UserInformation/selectors';
import { OutlineLinkButton } from 'components/OutlineButton';
import { phoneSize, tabletSize } from '../../global-styles';
import messages from './messages';
import { DISMISSED_STORAGE_KEY, isAffectedTimezone } from './utils';

const BannerWrapper = styled.div`
  width: 100%;
  padding: 24px 7.5%;
  background-color: #1f285b;
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 32px;

  @media (max-width: ${tabletSize}) {
    padding: 24px 40px;
  }

  @media (max-width: ${phoneSize}) {
    padding: 20px;
    align-items: flex-start;
    flex-direction: column;
  }
`;

const Title = styled.h2`
  color: #fff;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 8px;
`;

const Message = styled.p`
  color: #fff;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  line-height: 1.4;
  margin: 0;

  & + & {
    margin-top: 12px;
  }
`;

const CloseButton = styled(OutlineLinkButton)`
  box-shadow: none;
  flex-shrink: 0;
  margin: 0;
  padding: 10px 32px;
`;

export const TimezoneChangeBanner = ({ user }) => {
  const { pod12026TimezoneChangeBanner = false } = useFlags();
  const [dismissedUserId, setDismissedUserId] = useState(() =>
    localStorage.getItem(DISMISSED_STORAGE_KEY),
  );

  const userId = user.get('id')?.toString();
  const isDismissed = Boolean(userId && dismissedUserId === userId);
  const shouldShow =
    pod12026TimezoneChangeBanner &&
    isAffectedTimezone(user.get('timezone')) &&
    !isDismissed;

  const dismiss = () => {
    if (!userId) return;

    localStorage.setItem(DISMISSED_STORAGE_KEY, userId);
    setDismissedUserId(userId);
  };

  if (!shouldShow) return null;

  return (
    <BannerWrapper data-testid="timezone-change-banner">
      <div>
        <Title>
          <FormattedMessage {...messages.title} />
        </Title>
        <Message>
          <FormattedMessage
            {...messages.message}
            values={{ b: (chunks) => <strong>{chunks}</strong> }}
          />
        </Message>
      </div>
      <CloseButton as="button" onClick={dismiss} type="button">
        <FormattedMessage {...messages.close} />
      </CloseButton>
    </BannerWrapper>
  );
};

TimezoneChangeBanner.propTypes = {
  user: PropTypes.instanceOf(Map).isRequired,
};

const mapStateToProps = createStructuredSelector({
  user: makeSelectUser(),
});

export default connect(mapStateToProps)(TimezoneChangeBanner);
